import React, { useCallback, useState } from 'react';
import Wrapper from "../../components/Wrapper";
import { Helmet } from "react-helmet";
import { file, menu, telecomunication, employee as employeeTranslate } from "../../translate";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Alert, Divider, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import Api from "../../Api";
import { greenTheme } from "../../helpers/style";
import * as XLSX from 'xlsx';

const { REACT_APP_SERVER } = process.env;
const primaryColor = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary;
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover;

const style = {
    bgcolor: 'background.paper',
    p: 4,
    overflow: 'auto',
    borderRadius: 3,
};

function EmployeeUpload() {
    const navigate = useNavigate();
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const [uploading, setUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const handleFileChange = useCallback((ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                'text/csv'
            ];
            const validExtensions = ['.xlsx', '.xls', '.csv'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            
            if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
                setSelectedFile(file);
            } else {
                toast.error(langIndex === 1 ? 'Please select an Excel file (.xlsx, .xls, or .csv)' : 
                    langIndex === 2 ? 'Пожалуйста, выберите файл Excel (.xlsx, .xls или .csv)' : 
                    'Խնդրում ենք ընտրել Excel ֆայլ (.xlsx, .xls կամ .csv)');
                ev.target.value = '';
            }
        }
    }, [langIndex]);

    const handleDownloadDemo = useCallback(() => {
        // Demo data structure
        const headers = [
            langIndex === 1 ? ['Name', 'Email', 'Internal Number', 'Include Number', 'Password', 'Is SIP'] :
            langIndex === 2 ? ['Имя', 'Email', 'Внутренний номер', 'Включенный номер', 'Пароль', 'SIP'] :
            ['Անուն', 'Email', 'Ներքին համար', 'Ներառված համար', 'Գաղտնաբառ', 'SIP']
        ];
        
        const demoData = [
            ['John Doe', 'john.doe@example.com', '100', '12345678', 'password123', '1'],
            ['Jane Smith', 'jane.smith@example.com', '23', '87654321', 'password456', '1'],
        ];

        const data = [headers[langIndex - 1], ...demoData];
        
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Employees');
        
        const fileName = langIndex === 1 ? 'employees_demo.xlsx' : 
                        langIndex === 2 ? 'сотрудники_пример.xlsx' : 
                        'աշխատակիցներ_օրինակ.xlsx';
        
        XLSX.writeFile(wb, fileName);
    }, [langIndex]);

    const handleUpload = useCallback(async () => {
        if (!selectedFile) {
            toast.error(langIndex === 1 ? 'Please select a file first' : 
                langIndex === 2 ? 'Пожалуйста, сначала выберите файл' : 
                'Խնդրում ենք նախ ընտրել ֆայլ');
            return;
        }

        setUploading(true);
        try {
            await Api.employeeUpload(selectedFile);
            
            toast.success(langIndex === 1 ? 'File uploaded successfully' : 
                langIndex === 2 ? 'Файл успешно загружен' : 
                'Ֆայլը հաջողությամբ վերբեռնվեց');
            navigate('/employees');
        } catch (e) {
            console.error('Upload error:', e);
            toast.error(langIndex === 1 ? 'Error uploading file: ' + (e?.response?.data?.message || e?.message || 'Unknown error') : 
                langIndex === 2 ? 'Ошибка загрузки файла: ' + (e?.response?.data?.message || e?.message || 'Неизвестная ошибка') : 
                'Ֆայլի վերբեռնման սխալ: ' + (e?.response?.data?.message || e?.message || 'Անհայտ սխալ'));
        } finally {
            setUploading(false);
        }
    }, [selectedFile, navigate, langIndex]);

    return (
        <>
            <Helmet>
                <title>
                    {langIndex === 1 ? 'Upload Employees' : langIndex === 2 ? 'Загрузить сотрудников' : 'Վերբեռնել աշխատակիցներ'} - {menu.worker[langIndex]}
                </title>
            </Helmet>
            <Wrapper>
                <div className="tableParent">
                    <Paper sx={style}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
                            <IconButton
                                size="small"
                                sx={{ color: primaryColor, fontSize: 18 }}
                                onClick={() => navigate('/employees')}
                                aria-label="Back"
                            >
                                <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                            </IconButton>
                            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, m: 0 }}>
                                {langIndex === 1 ? 'Upload Employees from Excel' : langIndex === 2 ? 'Загрузить сотрудников из Excel' : 'Վերբեռնել աշխատակիցներ Excel-ից'}
                            </Typography>
                        </Box>

                        <Alert
                            icon={<InfoIcon />}
                            severity="success"
                            sx={
                                REACT_APP_SERVER !== 'ATS'
                                    ? { mb: 3 }
                                    : {
                                        mb: 3,
                                        border: `1px solid ${primaryColor}`,
                                        backgroundColor: `${primaryColor}10`,
                                        '& .MuiAlert-icon': { color: primaryColor },
                                    }
                            }
                        >
                            <Typography variant="body2">
                                {langIndex === 1 ?
                                    'Please upload an Excel file (.xlsx, .xls, or .csv) containing employee data. Make sure the file follows the required format. Download the demo file below to see the expected format.' :
                                    langIndex === 2 ?
                                        'Пожалуйста, загрузите файл Excel (.xlsx, .xls или .csv) с данными сотрудников. Убедитесь, что файл соответствует требуемому формату. Скачайте демо-файл ниже, чтобы увидеть ожидаемый формат.' :
                                        'Խնդրում ենք վերբեռնել Excel ֆայլ (.xlsx, .xls կամ .csv) աշխատակիցների տվյալներով: Համոզվեք, որ ֆայլը համապատասխանում է պահանջվող ձևաչափին: Ներբեռնեք օրինակ ֆայլը ստորև՝ ցանկալի ձևաչափը տեսնելու համար:'
                                }
                            </Typography>
                        </Alert>

                        <Box sx={{ mb: 3, p: 3, backgroundColor: '#fff', borderRadius: 2, border: `1px solid ${primaryColor}` }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>
                                {langIndex === 1 ? 'Download Demo File' : langIndex === 2 ? 'Скачать демо-файл' : 'Ներբեռնել օրինակ ֆայլ'}
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadDemo}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: primaryColor,
                                    color: primaryColor,
                                    mb: 2,
                                    '&:hover': {
                                        borderColor: hoverColor,
                                        backgroundColor: `${primaryColor}10`,
                                    },
                                }}
                            >
                                {langIndex === 1 ? 'Download' : langIndex === 2 ? 'Скачать' : 'Ներբեռնել'}
                            </Button>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#212121' }}>
                                {langIndex === 1 ? 'File Format Explanation:' : langIndex === 2 ? 'Объяснение формата файла:' : 'Ֆայլի ձևաչափի բացատրություն:'}
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1, fontSize: '0.9rem', color: '#424242' } }}>
                                <li>
                                    <strong>{langIndex === 1 ? 'Name' : langIndex === 2 ? 'Имя' : 'Անուն'}:</strong> {langIndex === 1 ? 'Employee full name (e.g., John Doe)' : langIndex === 2 ? 'Полное имя сотрудника (например, Иван Иванов)' : 'Աշխատակցի ամբողջական անուն (օր. Հովհաննես Հովհաննիսյան)'}
                                </li>
                                <li>
                                    <strong>Email:</strong> {langIndex === 1 ? 'Employee email address' : langIndex === 2 ? 'Адрес электронной почты сотрудника' : 'Աշխատակցի էլ. փոստի հասցե'}
                                </li>
                                <li>
                                    <strong>{langIndex === 1 ? 'Internal Number' : langIndex === 2 ? 'Внутренний номер' : 'Ներքին համար'}:</strong> {langIndex === 1 ? 'Internal short number (e.g., 100 or 23)' : langIndex === 2 ? 'Внутренний короткий номер (например, 100 или 23)' : 'Ներքին կարճ համար (օր. 100 կամ 23)'}
                                </li>
                                <li>
                                    <strong>{langIndex === 1 ? 'Include Number' : langIndex === 2 ? 'Включенный номер' : 'Ներառված համար'}:</strong> {langIndex === 1 ? 'Include/redirect number (8 digits, e.g., 12345678)' : langIndex === 2 ? 'Включенный/перенаправляющий номер (8 цифр, например, 12345678)' : 'Ներառված/վերահղման համար (8 նիշ, օր. 12345678)'}
                                </li>
                                <li>
                                    <strong>{langIndex === 1 ? 'Password' : langIndex === 2 ? 'Пароль' : 'Գաղտնաբառ'}:</strong> {langIndex === 1 ? 'SIP password (optional)' : langIndex === 2 ? 'SIP пароль (необязательно)' : 'SIP գաղտնաբառ (ընտրովի)'}
                                </li>
                                <li>
                                    <strong>SIP:</strong> {langIndex === 1 ? 'Is SIP enabled? (1 for yes, 0 for no)' : langIndex === 2 ? 'Включен ли SIP? (1 - да, 0 - нет)' : 'SIP-ը միացված է? (1 - այո, 0 - ոչ)'}
                                </li>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <input
                                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                                style={{ display: 'none' }}
                                id="file-upload"
                                type="file"
                                onChange={handleFileChange}
                                disabled={uploading}
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<UploadFileIcon />}
                                    disabled={uploading}
                                    sx={{
                                        textTransform: 'none',
                                        borderColor: primaryColor,
                                        color: primaryColor,
                                        px: 3,
                                        py: 1.5,
                                        '&:hover': {
                                            borderColor: primaryColor,
                                            backgroundColor: `${primaryColor}10`,
                                        },
                                    }}
                                >
                                    {selectedFile ? selectedFile.name : (file.choose[langIndex] || 'Choose File')}
                                </Button>
                            </label>
                            {selectedFile && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {langIndex === 1 ? `Selected: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} KB)` :
                                        langIndex === 2 ? `Выбрано: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} КБ)` :
                                        `Ընտրված: ${selectedFile.name} (${(selectedFile.size / 1024).toFixed(2)} ԿԲ)`}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/employees')}
                                disabled={uploading}
                                sx={{
                                    textTransform: 'none',
                                    borderColor: '#9e9e9e',
                                    color: '#9e9e9e',
                                    '&:hover': {
                                        borderColor: '#757575',
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                {telecomunication.modal_button_cancel?.[langIndex] || 'Cancel'}
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={uploading || !selectedFile}
                                sx={{
                                    textTransform: 'none',
                                    backgroundColor: primaryColor,
                                    color: '#fff',
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        backgroundColor: hoverColor,
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#e0e0e0',
                                        color: '#9e9e9e',
                                    },
                                }}
                            >
                                {uploading ? 
                                    (langIndex === 1 ? 'Uploading...' : langIndex === 2 ? 'Загрузка...' : 'Վերբեռնվում է...') :
                                    (langIndex === 1 ? 'Upload' : langIndex === 2 ? 'Загрузить' : 'Վերբեռնել')
                                }
                            </Button>
                        </Box>
                    </Paper>
                </div>
            </Wrapper>
        </>
    );
}

export default EmployeeUpload;
